import React, { useState, useId, useRef, useMemo } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Typography } from 'design-system/src/components/Typography/Typography';
import toast from 'react-hot-toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { shortErrorCopy } from 'design-system/src/sharedCopy';
import Button from 'design-system/src/components/Button/Button';
import { client } from '../../api';
import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from '../SaveFiltersButton';
import { areStringArraysEqual } from '../../utils';
import { priorAuthReviewsQueryKey } from '../../queries';
import {
  sortOfficeNames,
  formatStandardDateLocaleString,
  formatStandardDate,
  getDropdownInitialValueFromSearchParams,
  getInitialMultiselectFilterValueFromCookie,
  getInitialTableColumnFilterValueFromCookie,
  updateTableColumnFilterValueCookie,
} from '../../utils';
import { PriorAuthSubmitModal } from './PriorAuthSubmitModal';
import { AuthContext } from '../../auth';
import { useDefaultFilters } from '../../hooks/useDefaultFilters';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  ColumnFiltersState,
  VisibilityState,
  getFilteredRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';

import type { Row } from '@tanstack/react-table';

import type { PriorAuthReviewSummary, PriorAuthReviewStatus } from '../../api';
import { TableMultiselectFilter } from '../TableDropdownFilter/TableDropdownFilter';
import {
  type MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { useFetchPortalTableFilters } from '../useFetchPortalTableFilters';

import { useNavigate, useParams } from 'react-router-dom';
import {
  ClickableRow,
  FilterBar,
  FilterBarRow,
  SearchInput,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  HeaderSortingIndicator,
  TableTabList,
  ResultCountContainer,
  ResultCount,
  FilterBarRowSection,
  DividerPipe,
  NoPaddingCheckbox,
} from '../Table';
import FormattedCell from '../FormattedCell';

import TableNullState, { TableNullStateType } from '../TableNullState/TableNullState';
import { useTableNullState } from '../../hooks/useTableNullState';

type UITaskStatus = 'Open' | 'Reviewed';

const columnHelper = createColumnHelper<PriorAuthReviewSummary>();

interface PriorAuthReviewTableProps {
  data: PriorAuthReviewSummary[];
}

const priorAuthReviewDateSortingFn = (
  a: Row<PriorAuthReviewSummary>,
  b: Row<PriorAuthReviewSummary>,
  columnId: string,
) => {
  return new Date(b.getValue(columnId)).getTime() - new Date(a.getValue(columnId)).getTime();
};

const priorAuthReviewPatientSortingFn = (
  a: Row<PriorAuthReviewSummary>,
  b: Row<PriorAuthReviewSummary>,
  columnId: string,
) => {
  const aValue = a.getValue(columnId) as { name: string; dob: string };
  const bValue = b.getValue(columnId) as { name: string; dob: string };
  return bValue.name.localeCompare(aValue.name);
};

const priorAuthReviewDrugSortingFn = (
  a: Row<PriorAuthReviewSummary>,
  b: Row<PriorAuthReviewSummary>,
  columnId: string,
) => {
  const aValue = a.getValue(columnId) as { name: string; dateReceived: string };
  const bValue = b.getValue(columnId) as { name: string; dateReceived: string };
  return bValue.name.localeCompare(aValue.name);
};

const priorAuthReviewTableMultiSelectFilter = (
  row: Row<PriorAuthReviewSummary>,
  columnId: string,
  filterValue: string[],
) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  // @ts-expect-error columnId will be one of the column heading keys
  return filterValue.includes(row.original[columnId]);
};

const getDefaultNullStateType = (status: string) => {
  if (status === 'Reviewed') {
    return TableNullStateType.NO_PRIOR_AUTH_REVIEWS_REVIEWED;
  } else {
    return TableNullStateType.NO_PRIOR_AUTH_REVIEWS_OPEN;
  }
};

const PriorAuthReviewsTable: React.FC<PriorAuthReviewTableProps> = ({ data }) => {
  const navigate = useNavigate();
  const { priorAuthReviewId } = useParams();
  const authContext = React.useContext(AuthContext);
  const userEmail = authContext.user?.email;
  const { data: filterOptions } = useFetchPortalTableFilters();
  const statusTabId = useId();
  const statusPanelId = useId();

  // Add search value state for controlled input
  const [searchValue, setSearchValue] = useState('');

  // Refs for TableMultiselectFilter components
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);

  const { defaultPrescriberNames } = useDefaultFilters();

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'status', value: 'Open' },
    {
      id: 'office_name',
      value: getInitialTableColumnFilterValueFromCookie('prior_auth_reviews', 'office_name', userEmail),
    },
    {
      id: 'prescriber_name',
      value: defaultPrescriberNames || [],
    },
  ]);

  const currentPrescriberFilter = (columnFilters.find((f) => f.id === 'prescriber_name')?.value as string[]) || [];
  const currentPrescriberFilterNpis = currentPrescriberFilter
    .map((name) => {
      const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
      return prescriber?.value;
    })
    .filter((npi): npi is string => npi !== undefined);
  const showSaveButton = useMemo(() => {
    // Convert defaultPrescriberNames to NPIs for comparison
    const defaultPrescriberNpis =
      defaultPrescriberNames
        ?.map((name) => {
          const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
          return prescriber?.value;
        })
        .filter((npi): npi is string => npi !== undefined) || [];

    return !areStringArraysEqual(currentPrescriberFilterNpis, defaultPrescriberNpis);
  }, [currentPrescriberFilterNpis, defaultPrescriberNames, filterOptions?.prescribers]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    // These columns are always hidden
    status: false,
    office_name: false,
    // The selection column is originally visible but becomes hidden when the status
    // filter is toggled (because you can't select accepted reviews)
    selection: true,
  });
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [allRowsSelected, setAllRowsSelected] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);

  const deselectAllRows = () => {
    setSelectedRowIds([]);
    setAllRowsSelected(false);
  };

  const getUIPriorAuthReviewStatus = (status: PriorAuthReviewStatus): UITaskStatus => {
    return status === 'submitted' ? 'Reviewed' : 'Open';
  };

  const queryClient = useQueryClient();
  const acceptPriorAuthReview = useMutation<{}, AxiosError<{ error: string }>, boolean>({
    mutationFn: async (attestationCheckboxIsChecked: boolean) => {
      const res = await client.post(`prior-auth-reviews/bulk/form/submit`, {
        prior_auth_review_ids: selectedRowIds,
        attestation_submitted: attestationCheckboxIsChecked,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.custom(() => (
        <Toast variant="success">{selectedRowIds.length === 1 ? 'Prior auth' : 'Prior auths'} accepted</Toast>
      ));
      setSelectedRowIds([]);
      setAllRowsSelected(false);
      return queryClient.invalidateQueries({ queryKey: priorAuthReviewsQueryKey() });
    },
    onError: (error) => {
      const errorMessage = error.response?.data.error ? error.response?.data.error : shortErrorCopy;
      toast.custom(() => <Toast variant="error">{errorMessage}</Toast>);
      // Invalidate the query on error in case some of the prior auths submitted did succeed
      return queryClient.invalidateQueries({ queryKey: priorAuthReviewsQueryKey() });
    },
  });

  const ToggleAllRowsSelectionButton = (
    <NoPaddingCheckbox
      aria-label={allRowsSelected ? 'unselect all rows' : 'select all rows'}
      checked={allRowsSelected}
      onClick={() => {
        const rowsNotRequiringSignature = table
          .getRowModel()
          .flatRows.filter((row) => !row.original.signature_required);

        if (selectedRowIds.length !== rowsNotRequiringSignature.length) {
          const rowIds = rowsNotRequiringSignature.map((row) => row.original.id);
          setSelectedRowIds(rowIds);
          setAllRowsSelected(true);
        } else {
          setSelectedRowIds([]);
          if (allRowsSelected) {
            setAllRowsSelected(false);
          }
        }
      }}
    />
  );

  const getToggleRowSelectionButton = (row: Row<PriorAuthReviewSummary>) => {
    const isChecked = selectedRowIds.includes(row.original.id);

    if (row.original.signature_required) {
      return (
        <Tooltip
          content="Prior auths that require a signature cannot be accepted from the Prior Auth Reviews page"
          childNotInteractive
        >
          <NoPaddingCheckbox aria-label="select row" checked={false} disabled={true} />
        </Tooltip>
      );
    }

    return (
      <NoPaddingCheckbox
        aria-label={isChecked ? 'unselect row' : 'select row'}
        checked={isChecked}
        onClick={() => {
          const indexOfRowInSelectedRows = selectedRowIds.indexOf(row.original.id);
          if (indexOfRowInSelectedRows !== -1) {
            const updatedSelectedRowIds = [...selectedRowIds];
            updatedSelectedRowIds.splice(indexOfRowInSelectedRows, 1);
            setSelectedRowIds(updatedSelectedRowIds);
            if (allRowsSelected) {
              setAllRowsSelected(false);
            }
          } else {
            const updatedSelectedRowIds = [...selectedRowIds, row.original.id];
            setSelectedRowIds(updatedSelectedRowIds);
            const rows = table.getRowModel().flatRows;
            if (updatedSelectedRowIds.length === rows.length) {
              setAllRowsSelected(true);
            }
          }
        }}
      />
    );
  };

  const columns = [
    columnHelper.display({
      // @ts-ignore this is a special column with a special button header
      header: ToggleAllRowsSelectionButton,
      id: 'selection',
      enableSorting: false,
      cell: (info: any) => {
        return getToggleRowSelectionButton(info.row);
      },
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('created_at', {
      header: 'Date',
      sortingFn: priorAuthReviewDateSortingFn,
    }),
    columnHelper.accessor(
      (row) => {
        return { name: row.patient_name, dob: row.patient_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        sortingFn: priorAuthReviewPatientSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().name}
            subtitle={`DOB: ${formatStandardDate(info.getValue().dob)}`}
          />
        ),
      },
    ),
    columnHelper.accessor(
      (row) => {
        return {
          name: row.drug_name,
          dateReceived: row.prescription_recieved_at,
        };
      },
      {
        header: 'Drug',
        sortingFn: priorAuthReviewDrugSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            // TODO: include both drug name and description (currently has description only)
            title={info.getValue().name}
            subtitle={`Prescribed ${formatStandardDateLocaleString(info.getValue().dateReceived)}`}
          />
        ),
      },
    ),
    columnHelper.accessor('prescriber_name', {
      header: 'Prescriber',
      filterFn: priorAuthReviewTableMultiSelectFilter,
    }),

    // these columns are only used for filtering, not displaying
    columnHelper.accessor((row) => getUIPriorAuthReviewStatus(row.status), {
      id: 'status',
      header: 'Status',
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('office_name', {
      header: 'Office',
      enableGlobalFilter: false,
      filterFn: priorAuthReviewTableMultiSelectFilter,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    enableGlobalFilter: true,
    // Explicitly define the global filter function so that it works despite the formatted cells
    globalFilterFn: (row, _, filterValue) => {
      if (!filterValue || typeof filterValue !== 'string') return true;

      const searchTerm = filterValue.toLowerCase();

      if (row.original.patient_name?.toLowerCase().includes(searchTerm)) return true;

      if (row.original.prescriber_name?.toLowerCase().includes(searchTerm)) return true;

      if (row.original.drug_name?.toLowerCase().includes(searchTerm)) return true;

      const createdAtStr = formatStandardDateLocaleString(row.original.created_at);
      if (createdAtStr.toLowerCase().includes(searchTerm)) return true;

      const dobStr = formatStandardDate(row.original.patient_dob);
      if (dobStr.toLowerCase().includes(searchTerm)) return true;

      const dateReceivedStr = formatStandardDateLocaleString(row.original.prescription_recieved_at);
      if (dateReceivedStr.toLowerCase().includes(searchTerm)) return true;

      return false;
    },
    onColumnVisibilityChange: setColumnVisibility,
  });

  const nullStateType = useTableNullState({
    columnFilters,
    table,
    filterIds: ['office_name', 'prescriber_name'],
    defaultNullStateType: getDefaultNullStateType(table.getColumn('status')!.getFilterValue() as string),
    globalFilter: searchValue,
  });

  const clearAllFilters = React.useCallback(() => {
    // Clear all column filters except status (which is controlled by tabs)
    const statusFilter = columnFilters.find((f) => f.id === 'status');
    setColumnFilters(statusFilter ? [statusFilter] : []);

    // Clear global filter and search input
    table.setGlobalFilter('');
    setSearchValue('');

    // Clear cookies for saved filter values
    updateTableColumnFilterValueCookie([], 'prior_auth_reviews', 'office_name', userEmail);

    // Clear TableMultiselectFilter refs
    officeFilterRef.current?.clear();
    prescriberFilterRef.current?.clear();
  }, [columnFilters, table, userEmail]);

  const resultCountSection = (
    <FilterBarRowSection>
      <ResultCountContainer>
        <ResultCount>
          {table.getRowModel().rows?.length} {table.getRowModel().rows?.length === 1 ? 'result' : 'results'}
        </ResultCount>
      </ResultCountContainer>
    </FilterBarRowSection>
  );

  return (
    <>
      <TableContainer>
        <FilterBar>
          <FilterBarRow>
            <TableTabList
              tabs={[{ value: 'Open' }, { value: 'Reviewed' }]}
              selectedValue={(table.getColumn('status')!.getFilterValue() as string) || 'Open'}
              onSelect={(value) => {
                setColumnFilters((prev) =>
                  prev.map((filter) => (filter.id === 'status' ? { ...filter, value } : filter)),
                );
                setColumnVisibility((prev) => ({
                  ...prev,
                  selection: value === 'Open',
                }));
              }}
              tabListId={statusTabId}
              tabPanelId={statusPanelId}
              ariaLabel="Filter prior auth reviews by status"
            />
          </FilterBarRow>

          <FilterBarRow $reorderOnSmall>
            {!!selectedRowIds.length ? (
              <>
                <Button size="sm" onClick={() => setShowAcceptModal(true)}>
                  Accept
                </Button>
                <Button size="sm" variant="tertiary" onClick={deselectAllRows}>
                  Deselect
                </Button>
                <DividerPipe />
                <Typography styledAs="bodySmallSpaceGrotesk">{selectedRowIds.length} selected</Typography>
              </>
            ) : (
              <>
                <SearchInput
                  label="Search by patient name or DOB"
                  hideLabel
                  value={searchValue}
                  onChange={(e) => {
                    const value = (e.target as HTMLInputElement).value;
                    setSearchValue(value);
                    table.setGlobalFilter(value);
                  }}
                />
                <TableMultiselectFilter
                  ref={officeFilterRef}
                  options={filterOptions?.offices || []}
                  label={'Office'}
                  initialValue={getInitialMultiselectFilterValueFromCookie(
                    'prior_auth_reviews',
                    'office_name',
                    userEmail,
                  )}
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedValuesList = value.map((valueItem) => valueItem.value);
                    table.getColumn('office_name')!.setFilterValue(selectedValuesList);
                    updateTableColumnFilterValueCookie(value, 'prior_auth_reviews', 'office_name', userEmail);
                  }}
                  sortFn={sortOfficeNames}
                />
                <TableMultiselectFilter
                  ref={prescriberFilterRef}
                  options={filterOptions?.prescribers || []}
                  label={'Prescriber'}
                  initialValue={
                    getDropdownInitialValueFromSearchParams(
                      authContext.user?.default_filters,
                      '',
                      'providers',
                      filterOptions?.prescribers,
                    ) as MultiselectAutocompleteOption[]
                  }
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedNamesList = value.map((valueItem) => valueItem.description);
                    table.getColumn('prescriber_name')!.setFilterValue(selectedNamesList);
                  }}
                />
                <SaveFiltersButton $visible={showSaveButton} onClick={() => setShowSaveModal(true)} />
              </>
            )}

            {resultCountSection}
          </FilterBarRow>
        </FilterBar>

        <Table role="tabpanel" id={statusPanelId} aria-labelledby={statusTabId}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHeader
                      key={header.id}
                      onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                      $sortable={header.column.getCanSort()}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <HeaderSortingIndicator
                          isSorted={header.column.getIsSorted()}
                          sortDirection={header.column.getIsSorted()}
                        />
                      )}
                    </TableHeader>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <ClickableRow
                  $selected={row.original.id === priorAuthReviewId}
                  $new={row.original.status === 'unreviewed'}
                  key={row.original.id}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      onClick={cell.column.id === 'selection' ? undefined : () => navigate(row.original.id)}
                      onKeyDown={
                        cell.column.id === 'selection'
                          ? undefined
                          : (event: React.KeyboardEvent<HTMLTableCellElement>) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                navigate(row.original.id);
                              }
                            }
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </ClickableRow>
              ))
            ) : (
              <tr>
                <TableCell colSpan={columns.length}>
                  <TableNullState nullStateType={nullStateType} onClearFilters={clearAllFilters} />
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      {showAcceptModal && !!selectedRowIds.length ? (
        <PriorAuthSubmitModal
          onClose={() => setShowAcceptModal(false)}
          onSubmit={(checked) => acceptPriorAuthReview.mutate(checked)}
          isSubmitting={acceptPriorAuthReview.isPending}
        />
      ) : null}

      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            { label: 'Prescriber', displayValues: currentPrescriberFilter, saveValues: currentPrescriberFilterNpis },
          ]}
        />
      )}
    </>
  );
};

export default PriorAuthReviewsTable;
